/**
 * Two-pick lookahead (§8, and the limitation recorded in NOTES.md §31).
 *
 * A score that evaluates one pick in isolation cannot express the comparison
 * that actually decides most picks:
 *
 *     take the quarterback now and a running back next turn
 *     versus
 *     take the running back now and a quarterback next turn
 *
 * Both leave you with the same two positions filled. Which is better depends
 * entirely on what survives to your next pick, and a greedy score has no way to
 * ask. The visible symptom was a cluster: with your RB, WR and TE rooms full and
 * no quarterback, *five* quarterbacks floated into the top ten, because each one
 * independently scored "your QB slot is open" and none of them could know you
 * need exactly one. Weakening the roster-fit term did not fix it — the cluster
 * survived all the way down to a weight of 0.06, by which point the term had
 * been crushed for nothing.
 *
 * ## What this computes
 *
 *     lookahead(i) = max over positions p of (
 *         value to your roster, once i is on it,
 *         of the best p you would expect to still be there next turn
 *     )
 *
 * The important half is "once i is on it". Taking a quarterback fills your only
 * quarterback slot, so a second one is worth very little a round later and the
 * best follow-up is a back or a receiver. Taking a back leaves the slot open, so
 * the follow-up is a quarterback at full value. That difference is what
 * separates the first quarterback from the fifth, and it is invisible to any
 * one-pick score.
 *
 * ## Why this is affordable during a live draft
 *
 * Not Monte Carlo. The survivor model is precomputed once per engine pass, and
 * evaluating a candidate is a roster-fill walk over at most a roster's worth of
 * players plus one expectation per position. Only the top `LOOKAHEAD_CANDIDATES`
 * by base score are evaluated, because nothing below that can reach the top five
 * — so the whole stage costs a few thousand operations against the ~700-player
 * valuation pass it rides along with, and the 5-second poll does not notice.
 *
 * Pure module — no I/O.
 */

import type { LeagueConfig, PlayerId, Position } from './types.ts';
import { FANTASY_POSITIONS } from './types.ts';
import type { SurvivorModel } from './availability.ts';
import { rosterFitPoints, type BaselineResult } from './valuation.ts';

/**
 * How many candidates get a lookahead value.
 *
 * Everyone below this is scored as league-average on the term (a standardised
 * zero), which is the neutral choice rather than a penalty. The cliff that
 * creates sits far below the top five the term exists to reorder, and
 * `final_score` drives only the recommendation cards — the board itself is
 * ordered by the active source or by VOR, so nothing the user scrolls past
 * changes.
 */
export const LOOKAHEAD_CANDIDATES = 60;

export interface LookaheadInput {
  league: LeagueConfig;
  /** The user's roster before this pick. */
  roster: PlayerId[];
  positionOf: (id: PlayerId) => Position | null;
  baselines: BaselineResult;
  survivors: SurvivorModel;
}

/**
 * Build the evaluator. Returns a function of (candidate id, position, points)
 * so the caller can apply it to whichever slice of the pool it cares about.
 */
export function lookaheadValuer(
  input: LookaheadInput,
): (playerId: PlayerId, position: Position, projPoints: number) => number {
  const { league, roster, positionOf, baselines, survivors } = input;

  return (playerId: PlayerId, position: Position, projPoints: number): number => {
    if (survivors.targetPick === null) return 0;

    // The roster as it would be *after* this pick. This is the whole mechanism:
    // it is what makes a second quarterback worth less than the first.
    const after = [...roster, playerId];
    const fitAfter = rosterFitPoints(after, league, (id) => (id === playerId ? position : positionOf(id)), baselines);

    let best = 0;
    for (const nextPosition of FANTASY_POSITIONS) {
      // The candidate is deliberately *not* removed from his own position's
      // pool here, even though he is off the board in this branch.
      //
      // Removing him was tried. It is more literally correct and it is a bad
      // trade: it is a second-order effect on a pool 24 deep, it is swamped by
      // what the other eleven teams do between your turns, and it is
      // systematically biased against quality — taking the best back leaves a
      // slightly worse pool than taking the fourth-best, so the term quietly
      // penalises a player for being the best one available. On a board with no
      // ADP that was enough to rank RB2 above RB1 in the first round, which
      // reads as a bug because it is behaving like one. What this term is for
      // is the *positional* question — which room does this pick fill, and how
      // good is what is left in the others — and that answer does not depend on
      // one player's removal.
      const points = survivors.expectedBest(nextPosition);
      if (points === undefined) continue;

      const vor = points - (baselines.points[nextPosition] ?? 0);
      const value = vor + fitAfter(nextPosition, points);
      if (value > best) best = value;
    }

    return best;
  };
}
